import classNames from "classnames";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ADDON_MODAL_CONSTANTS,
  BUNDLE_ADDON_MODAL_STEP,
  MAX_BUNDLE_SIZE
} from "rivals/components/shared/AddonOptions/constants";
import RivalsButton from "rivals/components/shared/Button";
import CustomButton from "rivals/components/shared/CustomButton";
import SubscriptionPricing from "rivals/components/shared/Subscription/SubscriptionPricing";
import SubscriptionTerms from "rivals/components/shared/Subscription/SubscriptionTerms";
import {
  getAddOnPricing as getAddOnPricingAction,
  purchaseAddOn as purchaseAddOnAction
} from "rivals/redux/subscriptionAddons/actions";
import {
  getAddOnPricing,
  getBundle,
  getError
} from "rivals/redux/subscriptionAddons/selectors";
import { isPayPalSubscription } from "rivals/redux/user/selectors";
import { DECLINED_PAYMENT_ERRORS } from "rivals/shared/constants";
import {
  getAssociatedSubscriptionId,
  isAddOnSubscribed
} from "rivals/shared/utils/addons";
import { setData } from "rivals/shared/utils/qa";
import styles from "./index.module.scss";
import strings from "./strings";
import { AddOnPricing, Props } from "./types";

const { ALL_ACCESS } = ADDON_MODAL_CONSTANTS;

const {
  allAccessBillingPrompt,
  allAccessDeleteNotice,
  billingPrompt,
  genericErrorText,
  goback,
  paymentDeclinedText1,
  paymentDeclinedText2,
  cancel,
  submit,
  submitAgain,
  subscriptionChanges,
  updatePayment
} = strings;

const PurchaseStep = ({
  addOn,
  closeAndReset,
  existingSpyAddOns = [],
  onGoBack,
  parentPhilter,
  submitting
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const error = useSelector(getError);
  const bundle = useSelector(getBundle);
  const paymentDeclined = !!error && DECLINED_PAYMENT_ERRORS.includes(error);
  const existingSpyAddOnNames = existingSpyAddOns.map(addOn => addOn.name);
  const [localStorage, setLocalStorage] = useState<{
    [key: string]: AddOnPricing;
  }>({});
  const addOnPricingPayload = useSelector(getAddOnPricing);
  const [addOnPricing, setAddOnPricing] = useState(addOnPricingPayload);
  const [canSubscribe, setCanSubscribe] = useState(!isAddOnSubscribed(addOn));
  const isPayPal = useSelector(isPayPalSubscription);

  const handlePurchase = (): void => {
    const subscriptionId = getAssociatedSubscriptionId(addOn);
    const sitesIds =
      bundle && !!bundle.length
        ? bundle.map(o => o.siteId).filter(id => id)
        : [];

    if (canSubscribe && subscriptionId) {
      dispatch(purchaseAddOnAction(addOn.id, sitesIds));
    }
  };

  useEffect(() => {
    const subscriptionId = getAssociatedSubscriptionId(addOn);
    if (addOn && subscriptionId) {
      setCanSubscribe(true);
      if (localStorage[`${addOn.id}`] !== undefined) {
        setAddOnPricing(localStorage[`${addOn.id}`] as AddOnPricing);
      } else {
        dispatch(getAddOnPricingAction(subscriptionId, addOn.id));
      }
    } else {
      setCanSubscribe(false);
    }
    return;
  }, [addOn]);

  useEffect(() => {
    if (addOnPricingPayload) {
      const newStorage = Object.assign(localStorage, {
        [`${addOn.id}`]: addOnPricingPayload
      });
      setLocalStorage(newStorage);
      setAddOnPricing(addOnPricingPayload);
    }
  }, [addOnPricingPayload]);

  const errorSection = paymentDeclined ? (
    <div
      className={styles.errorText}
      {...setData(parentPhilter, "payment-declined")}
    >
      {paymentDeclinedText1}
      <a
        href={"/users/billing_information"}
        rel="noreferrer noopener"
        target="_blank"
        {...setData(parentPhilter, "update-billing-info-link")}
      >
        here
      </a>
      {paymentDeclinedText2}
    </div>
  ) : (
    <div
      className={styles.errorText}
      {...setData(parentPhilter, "generic-error")}
    >
      {genericErrorText}
    </div>
  );

  const allAccessCancellationNotice =
    existingSpyAddOnNames.length > 0 ? (
      <div
        className={styles.allAccessCancellationNotice}
        {...setData(parentPhilter, "all-access-cancellation-notice")}
      >
        {allAccessDeleteNotice(
          existingSpyAddOnNames.length < MAX_BUNDLE_SIZE
            ? existingSpyAddOnNames.join(" and ")
            : // eslint-disable-next-line no-magic-numbers
              `${existingSpyAddOnNames.slice(0, -1).join(", ")}, and ${
                existingSpyAddOnNames[existingSpyAddOnNames.length - 1]
              }`
        )}
      </div>
    ) : null;

  const SubscriptionPriceComponent = (): JSX.Element => {
    let tax = 0;
    let proratedPrice = Number(addOn.price);
    if (addOnPricing) {
      proratedPrice = addOnPricing.proratedPrice;
      tax = addOnPricing.tax;
    }

    return (
      <SubscriptionPricing
        addOn={addOn}
        bundle={bundle}
        isPayPal={isPayPal}
        isValid
        planType={addOn.plan}
        proratedPrice={proratedPrice}
        showAddOnOnly
        tax={tax}
        {...setData(parentPhilter, "pricing")}
      />
    );
  };

  return (
    <div className={styles.modalBody} {...setData(parentPhilter, "add-step")}>
      <div
        className={styles.addOnTitle}
        {...setData(parentPhilter, "change-summary-title")}
      >
        <div className={styles.addOnTitleWrapper}>
          {bundle && !!bundle.length && (
            <CustomButton
              ariaLabel="back"
              className={styles.gobackButton}
              onClick={(): void => onGoBack(BUNDLE_ADDON_MODAL_STEP)}
              philter={`${parentPhilter}-back`}
            />
          )}
          <span
            className={classNames({
              [styles.addonTitle]: bundle && !!bundle.length
            })}
          >
            {subscriptionChanges}
          </span>
        </div>
      </div>
      {addOn.type === ALL_ACCESS && allAccessCancellationNotice}
      <div
        className={styles.textPrompt}
        {...setData(parentPhilter, "billing-prompt")}
      >
        {addOn.type === ALL_ACCESS
          ? allAccessBillingPrompt(addOn.plan || "")
          : billingPrompt(addOn.plan || "")}
      </div>
      <div className={styles.billingContainer}>
        <SubscriptionPriceComponent />
      </div>
      <SubscriptionTerms />
      {error && errorSection}
      <div className={styles.buttons}>
        <RivalsButton
          buttonText={error ? submitAgain : submit}
          className={styles.submitButton}
          disabled={!canSubscribe || submitting}
          onClick={handlePurchase}
          size="small"
          {...setData(parentPhilter, "purchase-addon")}
        />
        {paymentDeclined ? (
          <a
            className={styles.updatePayment}
            href={"/users/billing_information"}
            rel="noreferrer noopener"
            target="_blank"
            {...setData(parentPhilter, "update-billing-info-button")}
          >
            {updatePayment}
          </a>
        ) : (
          <RivalsButton
            buttonText={bundle && !!bundle.length ? goback : cancel}
            className={styles.cancelButton}
            disabled={submitting}
            inverted
            onClick={(): void =>
              bundle && !!bundle.length
                ? onGoBack(BUNDLE_ADDON_MODAL_STEP)
                : closeAndReset(false)
            }
            size="small"
            {...setData(parentPhilter, "cancel-purchase")}
          />
        )}
      </div>
    </div>
  );
};

export default PurchaseStep;
